import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ChatService } from '@twaice-fe/frontend/shared/services';
import { SystemDetails } from '@twaice-fe/shared/models';
import { catchError, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { chatActions } from '../actions';
import { getSelected } from '../selectors/systems.selectors';

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error';
}

@Injectable()
export class ChatEffects {
  fetchChatMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.fetchChatMessages),
      withLatestFrom(this.store.select(getSelected)),
      filter((result): result is [Action<'[Chat/API] Fetch chat messages'>, SystemDetails] => !!result),
      switchMap(([, system]) =>
        this.chatService.fetchChatMessages(system.customerBk, system.systemBk).pipe(
          map((messages) => chatActions.loadChatMessagesSuccess({ messages })),
          catchError((error) => of(chatActions.loadChatMessagesFailure({ error: toErrorMessage(error) })))
        )
      )
    )
  );

  postChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.postChatMessage),
      withLatestFrom(this.store.select(getSelected)),
      filter(
        (
          result
        ): result is [
          {
            message: string;
            tags: string[];
            includeLink: boolean;
            parent?: string | undefined;
            relatedEntities?: string[] | undefined;
            timestamp?: Date | undefined;
          } & Action<'[Chat/API] Post chat message'>,
          SystemDetails,
        ] => !!result
      ),
      switchMap(([action, system]) =>
        this.chatService
          .postChatMessage(
            system.customerBk,
            system.systemBk,
            action.message,
            action.tags,
            action.includeLink,
            action.parent,
            action.relatedEntities,
            action.timestamp
          )
          .pipe(
            map((createdMessage) => chatActions.postChatMessageSuccess({ message: createdMessage })),
            tap(() => this.store.dispatch(chatActions.fetchChatMessages())),
            catchError((error) => of(chatActions.postChatMessageFailure({ error: toErrorMessage(error) })))
          )
      )
    )
  );

  patchChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.patchChatMessage),
      switchMap(({ customerBk, systemBk, timestamp, message, tags }) =>
        this.chatService.patchChatMessage(customerBk, systemBk, timestamp, message, tags).pipe(
          map((updatedMessage) => chatActions.patchChatMessageSuccess({ message: updatedMessage })),
          catchError((error) => of(chatActions.patchChatMessageFailure({ error: toErrorMessage(error) })))
        )
      )
    )
  );

  deleteChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.deleteChatMessage),
      switchMap(({ customerBk, systemBk, timestamp }) =>
        this.chatService.deleteChatMessage(customerBk, systemBk, timestamp).pipe(
          map(() => chatActions.deleteChatMessageSuccess({ timestamp })),
          catchError((error) => of(chatActions.deleteChatMessageFailure({ error: toErrorMessage(error) })))
        )
      )
    )
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private chatService: ChatService
  ) {}
}
